import { Navigate, useParams } from 'react-router-dom';
import { normalizeKindId, type SectionType } from '@gm-binder/shared';
import { useBinder } from '../../lib/useBinder';
import { EmptyState } from '../ui';
import {
  OverviewSection,
  RoomsSection,
  RoomDetail,
  CharactersSection,
  CharacterDetail,
  NpcsSection,
  CluesSection,
  ItemsSection,
  BestiarySection,
  EncountersSection,
  InitiativeSection,
  NotesSection,
  CursesSection,
  GallerySection,
  MusicSection,
  CompendiumSection,
  MarkdownPageSection,
} from '../sections';

/** Maps a built-in section type to its list component. */
const SECTION_COMPONENTS: Partial<Record<SectionType, () => JSX.Element | null>> = {
  overview: OverviewSection,
  rooms: RoomsSection,
  characters: CharactersSection,
  npcs: NpcsSection,
  clues: CluesSection,
  items: ItemsSection,
  bestiary: BestiarySection,
  encounters: EncountersSection,
  initiative: InitiativeSection,
  notes: NotesSection,
  curses: CursesSection,
  gallery: GallerySection,
  page: MarkdownPageSection,
  custom: MarkdownPageSection,
};

/** Kinds with no legacy SectionType that resolve to a runtime component by kind id. */
const KIND_COMPONENTS: Record<string, () => JSX.Element | null> = {
  music: MusicSection,
  compendium: CompendiumSection,
};

/** Section types that have a detail route (/:sectionId/:entityId). */
const DETAIL_COMPONENTS: Partial<Record<SectionType, () => JSX.Element | null>> = {
  rooms: RoomDetail,
  characters: CharacterDetail,
};

/**
 * Resolves the current section (by route param) within the loaded adventure and renders
 * the matching component. Renders a detail view when an entityId is present.
 */
export function SectionRouter() {
  const ctx = useBinder();
  const { sectionId, entityId } = useParams();

  if (!ctx) return null;
  const { adventure, instance } = ctx;

  const section = adventure.sections.find((s) => s.id === sectionId && s.enabled !== false);
  if (!section) {
    // Fall back to the first enabled section.
    const first = [...adventure.sections]
      .filter((s) => s.enabled !== false)
      .sort((a, b) => a.order - b.order)[0];
    if (first && sectionId !== first.id) {
      return <Navigate to={`/run/${instance.id}/${first.id}`} replace />;
    }
    return <EmptyState icon="📄" title="Sezione non trovata" />;
  }

  if (entityId) {
    const Detail = DETAIL_COMPONENTS[section.type];
    if (Detail) return <Detail />;
  }

  // New kinds (music/compendium) resolve by kind id; everything else by legacy type.
  const kindId = normalizeKindId(section.kind ?? section.type ?? 'custom');
  const KindComponent = KIND_COMPONENTS[kindId];
  if (KindComponent) return <KindComponent />;

  const Component = SECTION_COMPONENTS[section.type];
  if (!Component) return <EmptyState icon="📄" title="Tipo di sezione non supportato" />;
  return <Component />;
}

/** Redirects /run/:instanceId to its first enabled section. */
export function BinderIndexRedirect() {
  const ctx = useBinder();
  if (!ctx) return null;
  const first = [...ctx.adventure.sections]
    .filter((s) => s.enabled !== false)
    .sort((a, b) => a.order - b.order)[0];
  if (!first) return <EmptyState icon="📄" title="Nessuna sezione attiva" />;
  return <Navigate to={`/run/${ctx.instance.id}/${first.id}`} replace />;
}
